import { Autocomplete, Box, Button, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { CubeMemberFieldMatch, CubeMemberMatchField } from 'types/api/SystemTenant/AROCube/CubeBuilderTypes';

export const emptyMemberMatch = (): CubeMemberFieldMatch => ({
  sourceFieldId: '',
  destinationFieldId: '',
});
export function validMemberMatches(
  matches: CubeMemberFieldMatch[],
  sourceFields: CubeMemberMatchField[],
  destinationFields: CubeMemberMatchField[]
) {
  return (
    matches.length > 0 &&
    matches.every(
      (match) =>
        sourceFields.some((field) => field.value === match.sourceFieldId) &&
        destinationFields.some((field) => field.value === match.destinationFieldId)
    ) &&
    new Set(matches.map((match) => `${match.sourceFieldId}:${match.destinationFieldId}`)).size === matches.length
  );
}
export function MemberMatchConditions({
  value,
  onChange,
  sourceFields,
  destinationFields,
  disabled = false,
  readOnly = false,
}: {
  value: CubeMemberFieldMatch[];
  onChange: (value: CubeMemberFieldMatch[]) => void;
  sourceFields: CubeMemberMatchField[];
  destinationFields: CubeMemberMatchField[];
  disabled?: boolean;
  readOnly?: boolean;
}) {
  const update = (index: number, key: keyof CubeMemberFieldMatch, id: string) =>
    onChange(value.map((match, i) => (i === index ? { ...match, [key]: id } : match)));
  return (
    <Stack spacing={1.5}>
      <Tooltip title='Referenced members use their complete names. Empty fields do not match.'>
        <Typography
          variant='input-label-gray'
          component='span'
          tabIndex={0}
          sx={{ alignSelf: 'flex-start', fontSize: 12 }}
        >
          Match all conditions
        </Typography>
      </Tooltip>
      {value.map((match, index) => (
        <Box
          key={index}
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: readOnly ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) 32px',
              sm: `minmax(0, 1fr) 20px minmax(0, 1fr)${readOnly ? '' : ' 32px'}`,
            },
            gap: 1,
            alignItems: 'end',
          }}
        >
          <Autocomplete
            size='small'
            sx={{ minWidth: 0, gridColumn: 1 }}
            options={sourceFields}
            value={sourceFields.find((field) => field.value === match.sourceFieldId) ?? null}
            disabled={disabled || readOnly}
            getOptionLabel={(field) => field.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            onChange={(_, field) => update(index, 'sourceFieldId', field?.value ?? '')}
            renderInput={(params) => (
              <Stack spacing={0.75}>
                <Typography component='label' htmlFor={params.id} variant='input-label' fontWeight='bold'>
                  Source Field {index + 1}
                </Typography>
                <TextField {...params} placeholder='Select a field' />
              </Stack>
            )}
          />
          <Typography
            aria-hidden='true'
            color='text.secondary'
            sx={{
              display: { xs: 'none', sm: 'block' },
              gridColumn: 2,
              textAlign: 'center',
              lineHeight: '37px',
            }}
          >
            =
          </Typography>
          <Autocomplete
            size='small'
            sx={{ minWidth: 0, gridColumn: { xs: 1, sm: 3 } }}
            options={destinationFields}
            value={destinationFields.find((field) => field.value === match.destinationFieldId) ?? null}
            disabled={disabled || readOnly}
            getOptionLabel={(field) => field.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            onChange={(_, field) => update(index, 'destinationFieldId', field?.value ?? '')}
            renderInput={(params) => (
              <Stack spacing={0.75}>
                <Typography component='label' htmlFor={params.id} variant='input-label' fontWeight='bold'>
                  Destination Field {index + 1}
                </Typography>
                <TextField {...params} placeholder='Select a field' />
              </Stack>
            )}
          />
          {!readOnly && (
            <Tooltip title={`Remove condition ${index + 1}`}>
              <Box
                component='span'
                sx={{
                  gridColumn: { xs: 2, sm: 4 },
                  gridRow: { xs: '1 / span 2', sm: 1 },
                  pb: 0.25,
                }}
              >
                <IconButton
                  size='small'
                  aria-label={`Remove condition ${index + 1}`}
                  disabled={disabled || value.length === 1}
                  onClick={() => onChange(value.filter((_, i) => i !== index))}
                  sx={{ width: 32, height: 32, color: '#828C99' }}
                >
                  <DeleteOutlineRoundedIcon fontSize='small' />
                </IconButton>
              </Box>
            </Tooltip>
          )}
        </Box>
      ))}
      {!readOnly && (
        <Button
          variant='main-table-panel-border'
          startIcon={<AddCircleOutlineOutlinedIcon />}
          sx={{ alignSelf: 'flex-start', letterSpacing: 0 }}
          disabled={disabled}
          onClick={() => onChange([...value, emptyMemberMatch()])}
        >
          Add Condition
        </Button>
      )}
    </Stack>
  );
}
